// src/services/option-field-api.ts
// 拡張フィールド機能用のAPI通信サービス

import axios from 'axios';
import { ACS_LIGHT_API_BASE_PATH } from '../config';
import { isObjectData, setMasterCache, withMasterCache } from './storage/masterCache';
import { logApiError } from '../utils/errorLogger';
import { tokenManager } from './token-manager';

/**
 * 拡張フィールド型定義
 */
export interface OptionField {
  optionfieldno: number;
  targetfunction: number;
  optionfieldkey: string;
  dispname: string;
  optionfieldtype: number;
  disporder: number;
  optioninfo: string[] | null;
  searchitemuseflag: boolean;
  searchresultuseflag: boolean;
  custompositionflag: boolean;
  invalidflag: boolean;
  optionconfiginfoMap: Record<string, any>;
  javascriptcheckKindList: string[] | null;
  javascriptcheckDigit: number | null;
  defaultValue: string | null;
  format: string | null;
  prefix: string | null;
  suffix: string | null;
  cssClass: string | null;
  editscripttag: string | null;
  javaFormat: string | null;
  lineup: boolean;
  row: number | null;
  downloadFalseValue: string | null;
  downloadTrueValue: string | null;
  directInput: boolean;
  editscripttagmobile: string | null;
}

/**
 * 拡張フィールドリストレスポンス型定義
 */
export interface OptionFieldListResponse {
  statuscode: number;
  message: string;
  customeroptionfieldlist: OptionField[];
  followoptionfieldlist: OptionField[];
  contractitemoptionfieldlist: OptionField[];
}

// 拡張フィールド定義のキャッシュキー（通信環境が悪い時の初期表示ブロック回避用）
const MASTER_OPTION_FIELDS_KEY = 'masterOptionFields';

/**
 * 拡張フィールドAPI関連の関数をまとめたオブジェクト
 */
export const optionFieldAPI = {
  /**
   * 拡張フィールド一覧を取得する
   * @returns Promise<OptionFieldListResponse>
   */
  async getOptionFieldList(): Promise<OptionFieldListResponse> {
    // ネットワークから取得し、成功時（statuscode 200）はキャッシュへ保存する。
    // 返り値・throw 条件は従来どおり。
    const fetchFresh = async (): Promise<OptionFieldListResponse> => {
      try {
        const token = await tokenManager.getValidToken();

        if (!token) {
          throw new Error('認証情報がありません');
        }

        const response = await axios.post(`${ACS_LIGHT_API_BASE_PATH}/api2/optionfield/list`, {}, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        const data = response.data;

        // レスポンスが文字列の場合はJSONパース
        let parsedData;
        if (typeof data === 'string') {
          try {
            parsedData = JSON.parse(data);
          } catch (parseError) {
            throw new Error('Failed to parse response as JSON');
          }
        } else {
          parsedData = data;
        }

        // レスポンスの基本検証を緩和
        if (!parsedData || typeof parsedData !== 'object') {
          throw new Error('Invalid response format: not an object');
        }

        // statuscodeの存在確認（型チェックを緩和）
        if ('statuscode' in parsedData) {
          // 正常応答のみキャッシュ（通信環境が悪い時の初期表示ブロック回避用）
          if (parsedData.statuscode === 200) {
            setMasterCache(MASTER_OPTION_FIELDS_KEY, parsedData).catch(() => {});
          }
          return parsedData as OptionFieldListResponse;
        } else {
          throw new Error('Invalid response format: statuscode not found');
        }
      } catch (error) {
        console.error('拡張フィールドリスト取得エラー:', error);
        logApiError('optionFieldAPI.getOptionFieldList', error, `${ACS_LIGHT_API_BASE_PATH}/api2/optionfield/list`, 'POST');
        throw error;
      }
    };

    // キャッシュがあれば即返す。TTL 切れのときだけ裏で最新を取り直す。
    // キャッシュヒット時のみ throw しなくなる＝オフラインでも前回値で表示継続。
    return withMasterCache(MASTER_OPTION_FIELDS_KEY, isObjectData<OptionFieldListResponse>, fetchFresh);
  }
};

/**
 * 拡張フィールドの検索条件をAPI送信形式に変換
 * 通常フィールドと範囲検索フィールド（From/To）の両方を処理
 */
export const convertOptionFieldValuesToApiFormat = (
  values: Record<string, any>,
  fromValues: Record<string, any>,
  toValues: Record<string, any>,
  optionFields: OptionFieldListResponse | null
): string => {
  if (!optionFields?.customeroptionfieldlist) {
    return '';
  }

  const optionCustomer: Record<string, any> = {};
  const processedRangeFields = new Set<string>();

  // 範囲検索フィールドを処理（From/To を配列にまとめる）
  const rangeFieldKeys = new Set([
    ...Object.keys(fromValues),
    ...Object.keys(toValues)
  ]);

  rangeFieldKeys.forEach(fieldKey => {
    const fromValue = fromValues[fieldKey];
    const toValue = toValues[fieldKey];

    // From または To のいずれかに値がある場合のみ処理
    const hasFromValue = fromValue !== undefined && fromValue !== null && fromValue !== '';
    const hasToValue = toValue !== undefined && toValue !== null && toValue !== '';

    if (!hasFromValue && !hasToValue) {
      return;
    }

    const fieldDef = optionFields.customeroptionfieldlist.find(
      f => f.optionfieldkey === fieldKey
    );

    if (!fieldDef) {
      return;
    }

    optionCustomer[fieldKey] = {
      type: fieldDef.optionfieldtype,
      value: [fromValue || '', toValue || ''],
      issearchrangeselect: true
    };

    processedRangeFields.add(fieldKey);
  });

  // 通常の拡張フィールドを処理
  Object.entries(values).forEach(([fieldKey, value]) => {
    // 範囲検索として処理済みの場合はスキップ
    if (processedRangeFields.has(fieldKey)) {
      return;
    }

    // 値が空の場合はスキップ
    if (value === undefined || value === null || value === '') {
      return;
    }

    const fieldDef = optionFields.customeroptionfieldlist.find(
      f => f.optionfieldkey === fieldKey
    );

    if (!fieldDef) {
      return;
    }

    optionCustomer[fieldKey] = {
      type: fieldDef.optionfieldtype,
      value: value
    };
  });

  // JSON文字列化して返す
  const jsonString = JSON.stringify(optionCustomer);
  console.log('変換後の拡張フィールド検索条件:', jsonString);
  return jsonString;
};